import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ── Bring-your-own-app setup ────────────────────────────────────────────────
// Unlike a shared platform app, each tenant registers their own Meta/Instagram
// Developer App and enters its App ID/Secret here before connecting.
export const fetchUrls = createAsyncThunk('instagram/fetchUrls', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-instagram/urls');
    return res.data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch setup URLs'); }
});

export const fetchAppCredentials = createAsyncThunk('instagram/fetchAppCredentials', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-instagram/credentials');
    return res.data.data.credential;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch app credentials'); }
});

export const saveAppCredentials = createAsyncThunk('instagram/saveAppCredentials', async (payload, { rejectWithValue }) => {
  try {
    const res = await api.put('/meta-instagram/credentials', payload);
    return res.data.data.credential;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to save app credentials'); }
});

// ── Account (connect/disconnect) ───────────────────────────────────────────
export const fetchIGAccount = createAsyncThunk('instagram/fetchAccount', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-instagram/account');
    return res.data.data.account;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch account'); }
});

export const startConnect = createAsyncThunk('instagram/startConnect', async (loginType, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-instagram/oauth/start', { params: { loginType } });
    return res.data.data.url;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to start connection'); }
});

export const fetchPendingPages = createAsyncThunk('instagram/fetchPendingPages', async (pendingId, { rejectWithValue }) => {
  try {
    const res = await api.get(`/meta-instagram/oauth/pending/${pendingId}`);
    return res.data.data.pages;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Connection session expired'); }
});

export const finalizeConnect = createAsyncThunk('instagram/finalizeConnect', async ({ pendingId, pageId }, { rejectWithValue }) => {
  try {
    const res = await api.post('/meta-instagram/oauth/finalize', { pendingId, pageId });
    return res.data.data.account;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to finalize connection'); }
});

export const disconnectAccount = createAsyncThunk('instagram/disconnect', async (_, { rejectWithValue }) => {
  try {
    await api.post('/meta-instagram/disconnect');
    return true;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to disconnect'); }
});

// ── Comments ────────────────────────────────────────────────────────────────
export const fetchComments = createAsyncThunk('instagram/fetchComments', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-instagram/comments', { params });
    return { comments: res.data.data, media: res.data.meta?.media || {} };
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch comments'); }
});

export const replyToComment = createAsyncThunk('instagram/replyToComment', async ({ commentId, message, asPrivateReply }, { rejectWithValue }) => {
  try {
    await api.post(`/meta-instagram/comments/${commentId}/reply`, { message, asPrivateReply });
    return { commentId, message };
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to reply'); }
});

export const hideComment = createAsyncThunk('instagram/hideComment', async ({ commentId, hide }, { rejectWithValue }) => {
  try {
    await api.post(`/meta-instagram/comments/${commentId}/hide`, { hide });
    return { commentId, hide };
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to update comment'); }
});

export const deleteComment = createAsyncThunk('instagram/deleteComment', async (commentId, { rejectWithValue }) => {
  try {
    await api.delete(`/meta-instagram/comments/${commentId}`);
    return commentId;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to delete comment'); }
});

// ── Content publishing ───────────────────────────────────────────────────────
export const fetchPosts = createAsyncThunk('instagram/fetchPosts', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-instagram/posts', { params });
    return res.data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch posts'); }
});

// Live media fetched straight from Instagram's own Graph API — unlike
// fetchPosts (this app's own local composer-created records), this shows
// everything actually on the account, including posts published outside
// this app.
export const fetchLivePosts = createAsyncThunk('instagram/fetchLivePosts', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await api.get('/meta-instagram/posts/live', { params });
    return res.data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to fetch live Instagram posts'); }
});

export const uploadMedia = createAsyncThunk('instagram/uploadMedia', async (file, { rejectWithValue }) => {
  try {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/meta-instagram/media/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to upload media'); }
});

export const createPost = createAsyncThunk('instagram/createPost', async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post('/meta-instagram/posts', payload);
    return res.data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to create post'); }
});

export const publishPost = createAsyncThunk('instagram/publishPost', async (postId, { rejectWithValue }) => {
  try {
    const res = await api.post(`/meta-instagram/posts/${postId}/publish`);
    return res.data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to publish post'); }
});

export const schedulePost = createAsyncThunk('instagram/schedulePost', async ({ postId, scheduledAt, recurrence }, { rejectWithValue }) => {
  try {
    const res = await api.post(`/meta-instagram/posts/${postId}/schedule`, { scheduledAt, recurrence });
    return res.data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.error || 'Failed to schedule post'); }
});

// ── Slice ─────────────────────────────────────────────────────────────────
const instagramSlice = createSlice({
  name: 'instagram',
  initialState: {
    urls: null,
    credential: null,
    credentialLoading: false,
    account: null,
    accountLoading: false,
    pendingPages: [],
    connectUrl: null,
    comments: [],
    commentsMedia: {},
    commentsMeta: {},
    posts: [],
    postsMeta: {},
    livePosts: [],
    livePostsLoading: false,
    loading: false,
    error: null,
    // Cross-tab "new activity" badges for the Instagram Workspace's horizontal
    // nav — bumped by a socket listener that stays mounted across all 4 tabs
    // (InstagramDashboardLayout), cleared when the user actually opens that tab.
    unreadDMs: 0,
    unreadComments: 0,
  },
  reducers: {
    clearIGError: (state) => { state.error = null; },
    clearConnectUrl: (state) => { state.connectUrl = null; },
    bumpUnreadDMs: (state) => { state.unreadDMs += 1; },
    bumpUnreadComments: (state) => { state.unreadComments += 1; },
    clearUnreadDMs: (state) => { state.unreadDMs = 0; },
    clearUnreadComments: (state) => { state.unreadComments = 0; },
  },
  extraReducers: (builder) => {
    const pending = (state) => { state.loading = true; state.error = null; };
    const rejected = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      // Bring-your-own-app setup
      .addCase(fetchUrls.fulfilled, (state, action) => { state.urls = action.payload; })
      .addCase(fetchAppCredentials.pending, (state) => { state.credentialLoading = true; })
      .addCase(fetchAppCredentials.fulfilled, (state, action) => { state.credentialLoading = false; state.credential = action.payload; })
      .addCase(fetchAppCredentials.rejected, (state) => { state.credentialLoading = false; })
      .addCase(saveAppCredentials.fulfilled, (state, action) => { state.credential = action.payload; })
      .addCase(saveAppCredentials.rejected, rejected)

      // Account
      .addCase(fetchIGAccount.pending, (state) => { state.accountLoading = true; })
      .addCase(fetchIGAccount.fulfilled, (state, action) => { state.accountLoading = false; state.account = action.payload; })
      .addCase(fetchIGAccount.rejected, (state) => { state.accountLoading = false; })
      .addCase(startConnect.fulfilled, (state, action) => { state.connectUrl = action.payload; })
      .addCase(startConnect.rejected, rejected)
      .addCase(fetchPendingPages.fulfilled, (state, action) => { state.pendingPages = action.payload; })
      .addCase(fetchPendingPages.rejected, rejected)
      .addCase(finalizeConnect.fulfilled, (state, action) => { state.account = action.payload; state.pendingPages = []; })
      .addCase(finalizeConnect.rejected, rejected)
      .addCase(disconnectAccount.fulfilled, (state) => { state.account = null; })

      // Comments
      .addCase(fetchComments.pending, pending)
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.loading = false;
        state.comments = action.payload.comments;
        state.commentsMedia = action.payload.media;
      })
      .addCase(fetchComments.rejected, rejected)
      .addCase(replyToComment.fulfilled, (state, action) => {
        const c = state.comments.find((x) => x.commentId === action.payload.commentId);
        if (c) { c.repliedAt = new Date().toISOString(); c.replyText = action.payload.message; }
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        const c = state.comments.find((x) => x.commentId === action.payload.commentId);
        if (c) c.hidden = action.payload.hide;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.comments = state.comments.filter(
          (x) => x.commentId !== action.payload && x.parentCommentId !== action.payload
        );
      })

      // Posts
      .addCase(fetchPosts.pending, pending)
      .addCase(fetchPosts.fulfilled, (state, action) => { state.loading = false; state.posts = action.payload; })
      .addCase(fetchPosts.rejected, rejected)
      .addCase(fetchLivePosts.pending, (state) => { state.livePostsLoading = true; })
      .addCase(fetchLivePosts.fulfilled, (state, action) => { state.livePostsLoading = false; state.livePosts = action.payload; })
      .addCase(fetchLivePosts.rejected, (state) => { state.livePostsLoading = false; })
      .addCase(createPost.fulfilled, (state, action) => { state.posts.unshift(action.payload); })
      .addCase(createPost.rejected, rejected)
      .addCase(publishPost.fulfilled, (state, action) => {
        const idx = state.posts.findIndex((p) => p._id === action.payload._id);
        if (idx >= 0) state.posts[idx] = action.payload;
      })
      .addCase(publishPost.rejected, rejected)
      .addCase(schedulePost.fulfilled, (state, action) => {
        const idx = state.posts.findIndex((p) => p._id === action.payload._id);
        if (idx >= 0) state.posts[idx] = action.payload;
      })
      .addCase(schedulePost.rejected, rejected);
  },
});

export const {
  clearIGError, clearConnectUrl,
  bumpUnreadDMs, bumpUnreadComments, clearUnreadDMs, clearUnreadComments,
} = instagramSlice.actions;
export default instagramSlice.reducer;
